import { auth } from 'firebaseConfig';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword } from 'firebase/auth';

import { signInWithGoogleAndEnsureUserNode } from 'services/auth/GoogleAuthService';
import { sendPasswordReset } from 'services/auth/PasswordResetService';
import { patchNode, setNodeDoc } from 'services/nodes/NodeWriteService';
import { createSetting } from 'services/settings/SettingWriteService';
import { getUser, getUserWithEmail } from 'services/users/UserService';
import { createWorkspace } from 'services/workspaces/WorkspaceWriteService';

import { SectionType } from 'utilities/Enums';
import { getDefaultNode } from 'utilities/NodeFactory';
import { getSectionPath } from 'utilities/Routing';
import { generateSubstrings } from 'utilities/Strings';
import { buildUserSummary, getDefaultUser } from 'utilities/Users';

const buildSignupDebugContext = (stage, details = {}) => ({
  stage,
  at: new Date().toISOString(),
  ...details,
});

const mapPasswordResetRequestError = (error) => {
  const code = error?.code || '';

  if (code === 'auth/invalid-email') {
    return 'Please enter a valid email address.';
  }
  if (code === 'auth/too-many-requests') {
    return 'Too many attempts. Please wait a few minutes and try again.';
  }

  return 'Unable to send a reset email right now. Please try again.';
};

const useLoginAuthActions = ({
  loginFormValid,
  loginEmail,
  loginPassword,
  forgotFormValid,
  forgotEmail,
  markForgotSent,
  signupFormValid,
  signupEmail,
  signupName,
  signupPassword,
  postAuthRedirect,
  setPostAuthRedirect,
  setCurrentSection,
  showProgress,
  hideProgress,
  navigate,
  setErrorMessage,
  setUser,
  setUserSettings,
  setActiveWorkspace,
  setNewUserProfileSetup,
  setOpen,
}) => {
  const handleLogin = async () => {
    if (!loginFormValid) return;

    let userRecord = null;

    try {
      showProgress('Logging in...');

      userRecord = await getUserWithEmail(loginEmail.toLowerCase());

      if (userRecord?.data_profile.email) {
        await signInWithEmailAndPassword(auth, userRecord.data_profile.email, loginPassword);

        const fetchedUser = await getUser(auth.currentUser.uid);
        setUser(fetchedUser);

        const targetPath = postAuthRedirect;
        if (targetPath) {
          setPostAuthRedirect(null);
          navigate(targetPath, { replace: true });
        } else {
          setCurrentSection(SectionType.HOME);
          navigate(getSectionPath(SectionType.HOME));
        }
      } else {
        setErrorMessage('Invalid email or password.');
      }
    } catch (error) {
      setErrorMessage('Login failed. Please check your credentials.');
    } finally {
      hideProgress();
    }
  };

  const handleSignup = async () => {
    if (!signupFormValid) return;

    showProgress('Creating account...');

    const signupAttemptId = `signup:${Date.now()}`;
    console.info('[signup] start', buildSignupDebugContext('start', {
      signupAttemptId,
      email: signupEmail,
    }));

    try {
      console.info('[signup] auth:createUser:start', buildSignupDebugContext('auth:createUser:start', {
        signupAttemptId,
        email: signupEmail,
      }));
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        signupEmail,
        signupPassword
      );
      const authUser = userCredential.user;
      const uid = authUser.uid;
      console.info('[signup] auth:createUser:success', buildSignupDebugContext('auth:createUser:success', {
        signupAttemptId,
        uid,
        currentUserUid: auth.currentUser?.uid || null,
      }));

      const profile = getDefaultUser(
        uid,
        signupName,
        signupEmail.toLowerCase()
      );

      const defaultNode = getDefaultNode('USER');
      const index = generateSubstrings(signupName.toLowerCase(), 5);

      const profileNode = {
        ...defaultNode,
        type: 'USER',
        key: uid,
        userKey: uid,
        title: signupName,
        user: buildUserSummary({ data_profile: profile }),
        data_profile: profile,
        path: [],
        permissions: {
          VIEW: { type: 'EVERYONE' },
          CONTRIBUTE: { type: 'ME' },
          EDIT: { type: 'ME' },
          DELETE: { type: 'ME' },
          COMMENT: { type: 'EVERYONE' },
          SHARE: { type: 'EVERYONE' },
        },
        index,
      };

      const safePayload = JSON.parse(JSON.stringify(profileNode));
      console.info('[signup] userNode:create:start', buildSignupDebugContext('userNode:create:start', {
        signupAttemptId,
        uid,
      }));
      await setNodeDoc(
        uid,
        {
          ...safePayload,
        },
        { merge: false }
      );
      console.info('[signup] userNode:create:success', buildSignupDebugContext('userNode:create:success', {
        signupAttemptId,
        uid,
      }));

      const workspaceTitle = `${signupName}'s Space`;
      console.info('[signup] workspace:create:start', buildSignupDebugContext('workspace:create:start', {
        signupAttemptId,
        uid,
        workspaceTitle,
      }));
      const createdWorkspace = await createWorkspace(uid, workspaceTitle, true, {
        title: signupName,
        email: signupEmail,
      });
      console.info('[signup] workspace:create:result', buildSignupDebugContext('workspace:create:result', {
        signupAttemptId,
        uid,
        workspaceKey: createdWorkspace?.key || null,
        createdWorkspace,
      }));
      if (createdWorkspace?.key) {
        console.info('[signup] settings:create:start', buildSignupDebugContext('settings:create:start', {
          signupAttemptId,
          uid,
          workspaceKey: createdWorkspace.key,
        }));
        await createSetting(uid, { ACTIVE_WORKSPACE_KEY: createdWorkspace.key });
        console.info('[signup] settings:create:success', buildSignupDebugContext('settings:create:success', {
          signupAttemptId,
          uid,
          workspaceKey: createdWorkspace.key,
        }));
        setUserSettings && setUserSettings({ ACTIVE_WORKSPACE_KEY: createdWorkspace.key, userKey: uid });
        setActiveWorkspace && setActiveWorkspace(createdWorkspace);
        try {
          console.info('[signup] userNode:patchWorkspace:start', buildSignupDebugContext('userNode:patchWorkspace:start', {
            signupAttemptId,
            uid,
            workspaceKey: createdWorkspace.key,
          }));
          await patchNode(uid, { workspaceKey: createdWorkspace.key });
          console.info('[signup] userNode:patchWorkspace:success', buildSignupDebugContext('userNode:patchWorkspace:success', {
            signupAttemptId,
            uid,
            workspaceKey: createdWorkspace.key,
          }));
        } catch (error) {
          console.warn('[signup] userNode:patchWorkspace:failed', buildSignupDebugContext('userNode:patchWorkspace:failed', {
            signupAttemptId,
            uid,
            workspaceKey: createdWorkspace.key,
            code: error?.code || null,
            message: error?.message || String(error),
          }), error);
        }
      } else {
        console.warn('[signup] workspace:create:missingKey', buildSignupDebugContext('workspace:create:missingKey', {
          signupAttemptId,
          uid,
          createdWorkspace,
        }));
      }

      setUser({
        key: uid,
        email: authUser.email,
        displayName: authUser.displayName,
        emailVerified: authUser.emailVerified,
        phoneNumber: authUser.phoneNumber,
        userKey: uid,
        ...safePayload,
      });

      setNewUserProfileSetup(true);

      const targetPath = postAuthRedirect;
      if (targetPath) {
        setPostAuthRedirect(null);
        navigate(targetPath, { replace: true });
      } else {
        setCurrentSection(SectionType.HOME);
        navigate(getSectionPath(SectionType.HOME));
      }

      console.info('[signup] complete', buildSignupDebugContext('complete', {
        signupAttemptId,
        uid,
        workspaceKey: createdWorkspace?.key || null,
      }));

      setOpen(false);
    } catch (error) {
      console.error('[signup] failed', buildSignupDebugContext('failed', {
        signupAttemptId,
        code: error?.code || null,
        message: error?.message || String(error),
        currentUserUid: auth.currentUser?.uid || null,
      }), error);
      setErrorMessage(error.message || 'Signup failed. Please try again.');
    } finally {
      hideProgress();
    }
  };

  const handleSendPasswordReset = async () => {
    if (!forgotFormValid) return;

    try {
      showProgress('Sending reset link...');
      setErrorMessage(null);

      await sendPasswordReset(forgotEmail);
      markForgotSent();
    } catch (error) {
      console.error('[auth] password reset request failed:', error);
      setErrorMessage(mapPasswordResetRequestError(error));
    } finally {
      hideProgress();
    }
  };

  const handleGoogleSignup = async () => {
    setErrorMessage(null);

    try {
      showProgress('Signing in with Google...');

      const { user, isNewUser } = await signInWithGoogleAndEnsureUserNode();
      setUser(user);
      if (isNewUser) {
        setNewUserProfileSetup(true);
        const userKey = user?.key || user?.userKey || null;
        const displayName = user?.data_profile?.name || user?.title || user?.displayName || user?.email || 'Workspace';
        if (userKey) {
          try {
            const workspaceTitle = `${displayName}'s Space`;
            const createdWorkspace = await createWorkspace(userKey, workspaceTitle, true, user);
            if (createdWorkspace?.key) {
              await createSetting(userKey, { ACTIVE_WORKSPACE_KEY: createdWorkspace.key });
              setUserSettings && setUserSettings({ ACTIVE_WORKSPACE_KEY: createdWorkspace.key, userKey });
              setActiveWorkspace && setActiveWorkspace(createdWorkspace);
              await patchNode(userKey, { workspaceKey: createdWorkspace.key });
            }
          } catch (workspaceError) {
            console.warn('[auth] failed to create default workspace for Google user:', workspaceError);
          }
        }
      }

      const targetPath = postAuthRedirect;
      if (targetPath) {
        setPostAuthRedirect(null);
        navigate(targetPath, { replace: true });
      } else {
        setCurrentSection(SectionType.HOME);
        navigate(getSectionPath(SectionType.HOME));
      }

      setOpen(false);
    } catch (error) {
      console.error('[auth] Google signup failed:', error);
      setErrorMessage(error?.message || 'Google signup failed. Please try again.');
    } finally {
      hideProgress();
    }
  };

  return {
    handleLogin,
    handleSignup,
    handleSendPasswordReset,
    handleGoogleSignup,
  };
};

export default useLoginAuthActions;
